/**
 * Dialog Utility - Provides consistent toast, alert, confirm, and modal functionality
 * Uses React-based DialogProvider when available, falls back to browser dialogs
 */

import * as React from 'react';
import { toast, ToastOptions } from 'react-toastify';
import { getGlobalDialog } from '../../context/DialogContext';

export interface ToastConfig {
  message: string;
  title?: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  config?: ToastOptions;

}

const defaultToastOptions: ToastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
}

export interface ModalAction {
  label: string;
  onPress: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
}

export interface ModalOptions {
  title: string;
  content: string | React.ReactElement;
  primaryAction?: ModalAction;
  secondaryAction?: ModalAction;
  closeOnBackdropPress?: boolean;
  variant?: 'info' | 'warning' | 'error' | 'success';
}

class DialogUtil {
  /**
   * Show a simple alert dialog
   */
  showAlert(title: string, message: string): void {
    const dlg = getGlobalDialog();
    if (dlg) {
      dlg.alert({ title, message });
    } else {
      alert(`${title}\n\n${message}`);
    }
  }

  toast(message: string, type: 'success' | 'error' | 'warning' | 'info', config: ToastConfig): ReturnType<typeof toast> {
    switch (type) {
      case 'success':
        return toast.success(message, config.config);
      case 'error':
        return toast.error(message, config.config);
      case 'warning':
        return toast.warning(message, config.config);
      case 'info':
      default:
        return toast.info(message, config.config);
    }
  }

  /**
   * Show a toast notification
   */
  showToast(options: ToastConfig | string): void {
    const config = typeof options === 'string'
      ? { message: options, type: 'info' as const, config: defaultToastOptions }
      : { type: 'info' as const, config: defaultToastOptions, ...options };

    // Enhanced console output with title
    const emoji = {
      success: '✅',
      error: '❌',
      warning: '⚠️',
      info: 'ℹ️'
    }[config.type];

    const title = config.title ? `${config.title}: ` : '';
    console.log(`${emoji} ${title}${config.message}`);

    // Show toast notification with the correct options
    const toastOptions = config.config || defaultToastOptions;
    this.toast(config.message, config.type, { ...config, config: toastOptions });

  }

  /**
   * Show a modal dialog using the React-based DialogProvider
   */
  showModal(options: ModalOptions): void {
    const dlg = getGlobalDialog();
    if (dlg) {
      dlg.showModal(options);
    } else {
      // Fallback to browser dialogs
      const contentText = typeof options.content === 'string' ? options.content : '[Complex Content]';
      const message = `${options.title}\n\n${contentText}`;

      if (options.primaryAction) {
        const confirmed = window.confirm(`${message}\n\nPress OK for: ${options.primaryAction.label}`);
        if (confirmed) {
          options.primaryAction.onPress();
        } else if (options.secondaryAction) {
          const secondary = window.confirm(`Press OK for: ${options.secondaryAction.label}`);
          if (secondary) {
            options.secondaryAction.onPress();
          }
        }
      } else {
        alert(message);
      }
    }
  }

  /**
   * Show a confirmation dialog - returns a Promise<boolean>
   * Uses React modal when available, falls back to window.confirm
   */
  async confirm(title: string, message: string, options?: {
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: 'default' | 'warning' | 'error';
  }): Promise<boolean> {
    const dlg = getGlobalDialog();
    if (dlg) {
      return dlg.confirm({ title, message, ...options });
    }
    return window.confirm(`${title}\n\n${message}`);
  }

  /**
   * Show a confirmation dialog (synchronous fallback)
   * @deprecated Use `confirm()` (async) instead for React-based dialogs
   */
  showConfirm(title: string, message: string): boolean {
    return window.confirm(`${title}\n\n${message}`);
  }

  /**
   * Show a feedback modal
   */
  showFeedbackModal(error: any): void {
    const dlg = getGlobalDialog();
    if (dlg) {
      dlg.alert({
        title: 'Error Report',
        message: `An error occurred: ${error.message || 'Unknown error'}\n\nPlease contact support if this persists.`,
        variant: 'error',
      });
    } else {
      const feedbackText = prompt(
        `Error Feedback\n\nAn error occurred. Please describe what you were trying to do:\n\nError: ${error.message || 'Unknown error'}`
      );

      if (feedbackText) {
        console.log('User feedback:', feedbackText, 'Error:', error);
        this.showToast({
          title: 'Feedback Submitted',
          message: 'Thank you for your feedback!',
          type: 'success'
        });
      }
    }
  }
}

// Export singleton instance
const dialogUtil = new DialogUtil();
export default dialogUtil;
